import clsx from 'clsx';

import {
    Card,
    CardButton,
    type CardButtonProps,
    CardLink,
    type CardLinkProps,
    Description,
    Heading,
    type IconDefinition,
    renderIcon,
} from '@gitbook/spine-react';

interface MediaCardProps
    extends Pick<CardLinkProps, 'href' | 'target' | 'onPress' | 'onClick'>,
        Pick<CardButtonProps, 'onPress'> {
    icon?: IconDefinition;
    title: React.ReactNode;
    details?: React.ReactNode;
    buttons?: React.ReactNode;
    className?: string;
}

/**
 * Card to represent an external content with an optional square icon. The card will display the icon and its content
 * horizontally, allowing for things like media links or embed cards.
 */
export function MediaCard(props: MediaCardProps) {
    const {
        icon,
        title,
        details,
        buttons,
        className: propClassName,
        onPress,
        href,
        target,
        onClick,
    } = props;
    const className = clsx('flex items-center gap-4 p-4', propClassName);
    const content = (
        <>
            {icon ? (
                <div className="flex shrink-0 text-secondary">{renderIcon(icon, { size: 32 })}</div>
            ) : null}
            <div className="min-w-24 flex-1">
                <Heading>{title}</Heading>
                {details ? <Description className="text-ui-small">{details}</Description> : null}
            </div>
            {buttons ? (
                <div className="flex flex-wrap items-center gap-2 empty:hidden">{buttons}</div>
            ) : null}
        </>
    );
    if (href) {
        return (
            <CardLink
                href={href}
                target={target}
                className={className}
                onPress={onPress}
                onClick={onClick}
            >
                {content}
            </CardLink>
        );
    }
    if (onPress) {
        return (
            <CardButton className={className} onPress={onPress}>
                {content}
            </CardButton>
        );
    }
    return <Card className={className}>{content}</Card>;
}
